import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { getAccessToken, refreshAccessToken } from './authStore'

// Auth endpoints must never trigger the refresh-and-retry loop: a 401 from login or refresh
// is a real failure, not an expired access token.
const AUTH_PATHS = ['/users/login', '/users/logout', '/users/refresh']

function isAuthPath(url?: string): boolean {
  return !!url && AUTH_PATHS.some((p) => url.includes(p))
}

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

/**
 * Attach request/response interceptors that inject the in-memory access token and, on a 401
 * from a protected endpoint, refresh once and retry the original request a single time.
 *
 * @param instance - the Axios instance to wire up
 * @param opts.onAuthFailure - called when refresh fails, so the app can clear user state
 * @param opts.appId - identifies this app to the backend so its refresh-token cookie doesn't
 *   collide with another app's when both are served from the same origin (see configureAuth)
 */
export function attachAuthInterceptors(
  instance: AxiosInstance,
  opts: { onAuthFailure?: () => void; appId?: string } = {},
) {
  instance.interceptors.request.use((config) => {
    const token = getAccessToken()
    if (token) {
      config.headers.set?.('Authorization', `Bearer ${token}`)
    }
    if (opts.appId) {
      config.headers.set?.('X-App-Id', opts.appId)
    }
    return config
  })

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config as RetriableConfig | undefined
      const status = error.response?.status

      if (status === 401 && original && !original._retry && !isAuthPath(original.url)) {
        original._retry = true
        const token = await refreshAccessToken()
        if (token) {
          original.headers.set?.('Authorization', `Bearer ${token}`)
          return instance(original)
        }
        // Refresh failed — clear app auth state; ProtectedRoute will redirect to sign-in.
        opts.onAuthFailure?.()
      }

      return Promise.reject(error)
    },
  )
}
